import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import api from '../config/api';
import toast from 'react-hot-toast';
import { BRAND } from '../config/brand';

const YANDEX_AUTH_URL = `${process.env.REACT_APP_API_URL || BRAND.baseUrl}/api/auth/yandex`;

const AuthModal = ({ isOpen, onClose, defaultMode = 'login' }) => {
    const [mode, setMode] = useState(defaultMode);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Keep internal mode in sync when provider changes defaultMode
    useEffect(() => {
        setMode(defaultMode);
    }, [defaultMode]);

    // Reset form when modal is opened
    useEffect(() => {
        if (isOpen) {
            setFormData({
                username: '',
                email: '',
                password: '',
                confirmPassword: ''
            });
            setShowPassword(false);
            setShowConfirmPassword(false);
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            if (mode === 'login') {
                // Use POST for login and send credentials in request body
                const response = await api.post('/api/auth/login', {
                    email: formData.email,
                    password: formData.password
                });

                if (response.data?.token) {
                    localStorage.setItem('token', response.data.token);
                    toast.success('Успешный вход!');
                    onClose();
                    // reload so AuthProvider picks up token from localStorage
                    window.location.reload();
                }
            } else {
                if (formData.password !== formData.confirmPassword) {
                    toast.error('Пароли не совпадают');
                    setIsLoading(false);
                    return;
                }

                // Use POST for registration
                const response = await api.post('/api/auth/register', {
                    username: formData.username,
                    email: formData.email,
                    password: formData.password
                });

                if (response.data?.token) {
                    localStorage.setItem('token', response.data.token);
                    toast.success('Аккаунт успешно создан!');
                    onClose();
                    window.location.reload();
                } else {
                    // Server didn't return token (e.g. email verification required)
                    toast.success('Аккаунт создан. Проверьте почту для подтверждения.');
                }
            }
        } catch (error) {
            console.error('Auth error:', error);
            const message = error?.response?.data?.error || error?.response?.data?.message || 'Произошла ошибка';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const switchMode = () => {
        setMode(mode === 'login' ? 'register' : 'login');
        setFormData({
            username: '',
            email: '',
            password: '',
            confirmPassword: ''
        });
    };

    const handleYandexAuth = () => {
        window.location.href = YANDEX_AUTH_URL;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {mode === 'login' ? 'Войти в аккаунт' : 'Создать аккаунт'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {mode === 'register' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Имя пользователя
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    placeholder="Введите имя пользователя"
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Введите email"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Пароль
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Введите пароль"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    {mode === 'register' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Подтвердите пароль
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    placeholder="Подтвердите пароль"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 rounded-lg font-semibold hover:from-primary-600 hover:to-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                {mode === 'login' ? 'Вход...' : 'Регистрация...'}
                            </div>
                        ) : (
                            mode === 'login' ? 'Войти' : 'Создать аккаунт'
                        )}
                    </button>

                    {/* Switch Mode */}
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={switchMode}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                            {mode === 'login'
                                ? 'Нет аккаунта? Создать'
                                : 'Уже есть аккаунт? Войти'
                            }
                        </button>
                    </div>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500">или</span>
                        </div>
                    </div>

                    {/* Yandex Auth Button */}
                    <button
                        type="button"
                        onClick={handleYandexAuth}
                        className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                        <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M13.4 2.096C10.746 2.096 8.6 4.242 8.6 6.896c0 1.087.354 2.087.95 2.9L2.096 13.4C.95 13.4 0 14.35 0 15.5s.95 2.1 2.096 2.1c1.146 0 2.096-.95 2.096-2.1 0-.354-.087-.687-.242-.996l7.454-3.604c.354.596.95.996 1.596.996.55 0 1-.45 1-1s-.45-1-1-1c-.354 0-.687.2-.85.5L7.2 8.896C7.2 8.896 7.2 8.896 7.2 8.896c-.146-.3-.242-.65-.242-1 0-1.65 1.35-3 3-3s3 1.35 3 3c0 .35-.096.7-.242 1l-3.604 7.454c.596.354.996.95.996 1.596 0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2c-.354 0-.687.2-.85.5L3.604-7.454c.3-.146.5-.5.5-.85 0-.55-.45-1-1-1s-1 .45-1 1c0 .354.2.687.5.85l7.454 3.604c-.354.596-.95.996-1.596.996-1.65 0-3-1.35-3-3z" />
                        </svg>
                        Войти через Яндекс
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AuthModal;
